import React, { useState, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import RouterLinkButton from 'components/router-link-button'
import useIsMobile from 'lib/hooks/use-is-mobile'
import { GET_COACH_AVAILABILITY_OVERRIDES } from '../custom-availability/queries'
import OneTimeSlotDialog from './one-time-slot-dialog'
import AddTimeMenu from './add-time-menu'
import AvailabilityCalendarGrid from './availability-calendar-grid'
import RecurringHoursSidebar from './recurring-hours-sidebar'
import WeekNavigator from './week-navigator'
import Legend from './legend'
import OverridesList from './overrides-list'
import BlocksList from './blocks-list'
import GoogleCalendarIntegration from '../../blocked-times/block-time-manager/google-calendar-integration'
import { getWeekRange, formatFacilityLabel } from './utils'

const useTabStyles = makeStyles(theme => ({
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
    [theme.breakpoints.only('xs')]: {
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
    },
  },
  facilityField: {
    flex: 1,
    minWidth: 0,
    maxWidth: 320,
    [theme.breakpoints.only('xs')]: {
      maxWidth: 'none',
      flexBasis: '100%',
    },
  },
  sectionCard: {
    padding: theme.spacing(3),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  sectionTitle: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  sectionAction: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
  callout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  calloutIcon: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  calendarSyncBox: {
    marginBottom: theme.spacing(2),
  },
  createMenuItemDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    boxSizing: 'border-box',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  createMenuItemDotOpen: {
    backgroundColor: '#FFFFFF',
    backgroundImage:
      'repeating-linear-gradient(135deg, #E3F2FD 0 4px, #FFFFFF 4px 8px)',
    border: '1px solid #90CAF9',
  },
  createMenuItemDotBlocked: {
    backgroundColor: '#F5F5F5',
    border: '1px solid #BDBDBD',
  },
  createMenuPaper: {
    minWidth: 220,
  },
}))

const GET_AVAILABILITY = gql`
  query CoachAvailability {
    coachAvailabilities {
      coachFacility {
        id
        name
      }
      startDate
      timeZone
      timeSlots {
        day
        startTime
        endTime
      }
    }
  }
`

const GET_BLOCK_TIMES_FOR_WEEK = gql`
  query CoachAvailabilityBlockTimesForWeek($datetimeRange: DateTimeRangeInput) {
    coachAvailabilityBlockTimes(datetimeRange: $datetimeRange) {
      id
      startTime
      endTime
      allDay
      blockNote
    }
  }
`

const SectionHeader = ({ title, description, action, classes }) => (
  <Box className={classes.sectionHeader}>
    <Box>
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.sectionDescription}>
        {description}
      </Typography>
    </Box>
    {action && <Box className={classes.sectionAction}>{action}</Box>}
  </Box>
)

const AvailabilityTab = ({
  refetchCoachOnboardingStatus,
  HeaderActionsPortal,
}) => {
  const tabClasses = useTabStyles()
  const isMobile = useIsMobile()
  const [selectedFacilityId, setSelectedFacilityId] = useState(null)
  const [referenceDate, setReferenceDate] = useState(() => DateTime.local())
  const weekRange = useMemo(() => getWeekRange(referenceDate), [referenceDate])
  const [createMenu, setCreateMenu] = useState(null)
  const [createDialog, setCreateDialog] = useState(null)

  const {
    data: availabilityData,
    loading: availabilityLoading,
    refetch: refetchAvailability,
  } = useQuery(GET_AVAILABILITY)

  const {
    data: overridesData,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useQuery(GET_COACH_AVAILABILITY_OVERRIDES, {
    variables: {
      dateRange: { from: DateTime.local().toISODate() },
    },
  })

  const { data: blocksData, refetch: refetchBlocks } = useQuery(
    GET_BLOCK_TIMES_FOR_WEEK,
    {
      variables: {
        datetimeRange: {
          from: weekRange.start.startOf('day').toISO(),
          to: weekRange.end.endOf('day').toISO(),
        },
      },
    },
  )

  const loading = availabilityLoading || overridesLoading

  if (loading) {
    return (
      <Box p={2}>
        <LinearProgress />
      </Box>
    )
  }

  if (isEmpty(availabilityData?.coachAvailabilities)) {
    return (
      <Box>
        <Typography variant="h6" color="textPrimary">
          You have not set up any facilities.
        </Typography>
        <Box mt={2}>
          <RouterLinkButton
            variant="contained"
            color="primary"
            to="/pga-coach/facilities/new"
          >
            Add Facility
          </RouterLinkButton>
        </Box>
      </Box>
    )
  }

  const availabilities = availabilityData.coachAvailabilities
  const selectedAvailability = selectedFacilityId
    ? availabilities.find(a => a.coachFacility.id === selectedFacilityId)
    : availabilities[0]

  const facilities = Array.from(
    new Map(
      availabilities.map(a => [a.coachFacility.id, a.coachFacility]),
    ).values(),
  )

  const allOverrides = overridesData?.coachAvailabilityOverrides || []
  const overridesForFacility = allOverrides.filter(
    o => o.coachFacility?.id === selectedAvailability.coachFacility.id,
  )

  const overridesForWeek = overridesForFacility.filter(o => {
    const d = DateTime.fromISO(o.date)
    return (
      d >= weekRange.start.startOf('day') && d <= weekRange.end.endOf('day')
    )
  })

  const blocksForWeek = blocksData?.coachAvailabilityBlockTimes || []

  const handleSuccess = () => {
    refetchAvailability()
    refetchOverrides()
    refetchBlocks()
    refetchCoachOnboardingStatus && refetchCoachOnboardingStatus()
  }

  const openCreateDialog = type => {
    if (!createMenu) return
    setCreateDialog({
      initialType: type,
      defaultDate: createMenu.date,
      defaultStartTime: createMenu.startTime,
    })
    setCreateMenu(null)
  }

  return (
    <Box>
      {HeaderActionsPortal && (
        <HeaderActionsPortal>
          <AddTimeMenu
            facilities={facilities}
            onSuccess={handleSuccess}
            compact={isMobile}
            defaultFacilityId={selectedAvailability.coachFacility.id}
          />
        </HeaderActionsPortal>
      )}

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          createMenu
            ? {
              left: createMenu.anchorPosition.left,
              top: createMenu.anchorPosition.top,
            }
            : undefined
        }
        open={Boolean(createMenu)}
        onClose={() => setCreateMenu(null)}
        classes={{ paper: tabClasses.createMenuPaper }}
      >
        <MenuItem onClick={() => openCreateDialog('open')}>
          <span
            className={`${tabClasses.createMenuItemDot} ${tabClasses.createMenuItemDotOpen}`}
            aria-hidden
          />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Add Open Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Bookable — one-time hours
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => openCreateDialog('block')}>
          <span
            className={`${tabClasses.createMenuItemDot} ${tabClasses.createMenuItemDotBlocked}`}
            aria-hidden
          />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Add Blocked Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Not bookable — vacation, time off
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {createDialog && (
        <OneTimeSlotDialog
          facilities={facilities}
          onSuccess={() => {
            handleSuccess()
            setCreateDialog(null)
          }}
          triggerless
          isOpen
          onClose={() => setCreateDialog(null)}
          initialType={createDialog.initialType}
          defaultFacilityId={selectedAvailability.coachFacility.id}
          defaultDate={createDialog.defaultDate}
          defaultStartTime={createDialog.defaultStartTime}
        />
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <Box className={tabClasses.calendarHeader}>
              <FormControl
                variant="outlined"
                size="small"
                className={tabClasses.facilityField}
              >
                {availabilities.length === 1 ? (
                  <TextField
                    disabled
                    size="small"
                    value={formatFacilityLabel(selectedAvailability)}
                    variant="outlined"
                  />
                ) : (
                  <TextField
                    select
                    size="small"
                    value={selectedAvailability.coachFacility.id}
                    variant="outlined"
                    label="Facility"
                    onChange={e => setSelectedFacilityId(e.target.value)}
                  >
                    {availabilities.map(a => (
                      <MenuItem
                        key={a.coachFacility.id}
                        value={a.coachFacility.id}
                      >
                        {formatFacilityLabel(a)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </FormControl>
              <Box flex={1} />
              <Button
                size="small"
                color="primary"
                onClick={() => setReferenceDate(DateTime.local())}
              >
                Today
              </Button>
            </Box>
            <WeekNavigator
              weekRange={weekRange}
              onPrev={() => setReferenceDate(referenceDate.minus({ weeks: 1 }))}
              onNext={() => setReferenceDate(referenceDate.plus({ weeks: 1 }))}
              onJumpToDate={picked => setReferenceDate(picked)}
            />
            <AvailabilityCalendarGrid
              weekRange={weekRange}
              recurringTimeSlots={selectedAvailability.timeSlots}
              overrides={overridesForWeek}
              blocks={blocksForWeek}
              timeZone={selectedAvailability.timeZone}
              onCellClick={info => setCreateMenu(info)}
            />
            <Legend />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <RecurringHoursSidebar
            availability={selectedAvailability}
            onSuccess={handleSuccess}
          />
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" className={tabClasses.sectionCard}>
            <SectionHeader
              classes={tabClasses}
              title="Open Time"
              description="Bookable. One-time openings outside your recurring hours — perfect for adding extra availability on a specific day."
              action={
                overridesForFacility.length > 0 ? (
                  <OneTimeSlotDialog
                    facilities={facilities}
                    onSuccess={handleSuccess}
                    initialType="open"
                    defaultFacilityId={selectedAvailability.coachFacility.id}
                  />
                ) : null
              }
            />
            <OverridesList
              overrides={overridesForFacility}
              onChanged={refetchOverrides}
              emptyAction={
                <OneTimeSlotDialog
                  tonal
                  facilities={facilities}
                  onSuccess={handleSuccess}
                  initialType="open"
                  defaultFacilityId={selectedAvailability.coachFacility.id}
                />
              }
            />
          </Card>
        </Grid>

        <Grid item xs={12} id="blocked-time">
          <Card variant="outlined" className={tabClasses.sectionCard}>
            <SectionHeader
              classes={tabClasses}
              title="Blocked Time"
              description="Not bookable. Block off vacations, time off, or other commitments. Blocked time overrides your recurring hours and any open time."
              action={
                blocksForWeek.length > 0 ? (
                  <OneTimeSlotDialog
                    facilities={facilities}
                    onSuccess={handleSuccess}
                    initialType="block"
                    defaultFacilityId={selectedAvailability.coachFacility.id}
                  />
                ) : null
              }
            />
            <Box className={tabClasses.callout}>
              <InfoOutlinedIcon
                fontSize="small"
                className={tabClasses.calloutIcon}
              />
              <Typography variant="body2">
                Students won't be able to book sessions with you during a
                blocked time.
              </Typography>
            </Box>
            <Box className={tabClasses.calendarSyncBox}>
              <GoogleCalendarIntegration onIntegrationChange={refetchBlocks} />
            </Box>
            <BlocksList
              blocks={blocksForWeek}
              onChanged={refetchBlocks}
              emptyAction={
                <OneTimeSlotDialog
                  tonal
                  facilities={facilities}
                  onSuccess={handleSuccess}
                  initialType="block"
                  defaultFacilityId={selectedAvailability.coachFacility.id}
                />
              }
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AvailabilityTab
